// @flow

import React, { type Node, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useOpenedIncomingPayment } from '@tectonics/prs/incoming-payment';
import { useUserInputAsRadioButton } from '@tectonics/prs/user-input-values';
import { useSubmitting } from '@tectonics/prs/submitting';
import { getOneClickCardOrCry } from '@tectonics/prs/one-click-card';
import { InputRadioField } from '../../../../molecules/input-radio-field';
import { formatOneClickCardInfoLabel } from '../commons/format-one-click-card-label';
import { DisplayBrand } from '../commons/display-brand';
import genericCardImageSrc from '../card-form/fields/card-icon/images/generic-card.svg';

export function CardTypeSelector(): Node {
  const { t } = useTranslation('volcanes');

  const incomingPayment = useOpenedIncomingPayment();
  const oneClickCard = getOneClickCardOrCry(incomingPayment);

  return (
    <Root data-test-name="card-type">
      <CardTypeRadioField
        value="oneclick"
        label={formatOneClickCardInfoLabel(oneClickCard)}
      />
      <CardTypeRadioField
        value="creditcard"
        label={t('payment-methods.card.oneClick.newCreditCardLabel')}
      />
    </Root>
  );
}

type Props = {|
  value: string,
  label: string,
|}

function CardTypeRadioField({ value, label }: Props): Node {
  const [selectedCardType, setSelectedCardType] = useUserInputAsRadioButton('selectedCardType');
  const isSubmitting = useSubmitting();
  const genericCardBrand = useMemo(() => (
    <DisplayBrand iconSrc={genericCardImageSrc} iconAlt="Card Icon" />
  ), []);

  return (
    <InputRadioField
      name="card-type"
      value={value}
      label={label}
      checked={selectedCardType === value}
      disabled={isSubmitting}
      onChange={setSelectedCardType}
      displayBrand={genericCardBrand}
    />
  );
}

const Root = styled.div`
  margin: 20px 0 40px 0;

  & > *:not(:first-child) {
    margin-top: 16px;
  }
`;
